import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Mail, Phone, MessageCircle } from 'lucide-react';
import { customerSchema, type CustomerFormData, type Customer } from '../../types/customer';
import { type SalesOrder } from '../../types/salesOrder';
import { salesOrderService } from '../../services/salesOrderService';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { formatCurrency } from '../../utils/format';
import { useSettingsStore } from '../../store/settingsStore';

import { generateCode } from '../../utils/codeGenerator';
import { useLanguage } from '../../features/language/useLanguage';

interface CustomerFormProps {
  initialData?: Customer;
  onSubmit?: (data: CustomerFormData) => Promise<void>;
  onCancel: () => void;
  readOnly?: boolean;
  existingCodes?: string[];
}

const EMAIL_MAX_LENGTH = 50;

const enforceEmailMaxLength = (value: string) => {
  return value.slice(0, EMAIL_MAX_LENGTH);
};

export const CustomerForm: React.FC<CustomerFormProps> = ({ 
  initialData, 
  onSubmit, 
  onCancel, 
  readOnly = false,
  existingCodes = [] 
}) => {
  const { t } = useLanguage();
  const { currencySymbol } = useSettingsStore();
  const [salesOrders, setSalesOrders] = useState<SalesOrder[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [isShippingSameAsBilling, setIsShippingSameAsBilling] = useState(false);

  const { register, handleSubmit, watch, setValue, formState: { errors, isSubmitting } } = useForm<CustomerFormData>({
    resolver: zodResolver(customerSchema),
    mode: 'onChange',
    defaultValues: initialData || {
      name: '',
      code: '',
      email: '',
      phone: '',
      billingAddress: '',
      shippingAddress: '',
      messageOnInvoice: '',
      internalNotes: ''
    }
  });

  const name = watch('name');
  const code = watch('code');
  const billingAddress = watch('billingAddress');
  const shippingAddress = watch('shippingAddress');

  // Auto-generate code when name changes, if code is empty/pristine and not in readOnly mode
  useEffect(() => {
    if (readOnly || initialData?.code) return; // Don't overwrite existing customers' codes or if readOnly
    
    // Only generate if user hasn't manually entered a code (or if code is empty)
    // Here we use a simple heuristic: if code is empty, generate it.
    // If user clears the code, it will regenerate.
    if (name && !code) {
      const generated = generateCode(name, existingCodes);
      if (generated) {
        setValue('code', generated, { shouldValidate: true });
      }
    }
  }, [name, code, readOnly, initialData, existingCodes, setValue]);

  useEffect(() => {
    if (readOnly) return;
    const billing = billingAddress || '';

    if (isShippingSameAsBilling) {
      if (shippingAddress !== billing) {
        setValue('shippingAddress', billing, { shouldValidate: true });
      }
    }
  }, [billingAddress, isShippingSameAsBilling, readOnly, setValue, shippingAddress]);

  useEffect(() => {
    const billing = initialData?.billingAddress || '';
    const shipping = initialData?.shippingAddress || '';
    if (!readOnly && initialData) {
      setIsShippingSameAsBilling(!shipping || shipping === billing);
    }
  }, [initialData, readOnly]);

  useEffect(() => {
    if (readOnly && initialData?.name) {
      loadHistory(initialData.name);
    }
  }, [readOnly, initialData]);

  const loadHistory = async (customerName: string) => {
    try {
      setLoadingHistory(true);
      const allSOs = await salesOrderService.getAll();
      const history = allSOs
        .filter(so => so.customer === customerName)
        .sort((a, b) => new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime());
      setSalesOrders(history);
    } catch (error) {
      console.error('Failed to load sales order history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const email = watch('email');
  const emailLength = email?.length || 0;
  const phone = watch('phone');
  const whatsappNumber = (phone || '').replace(/\D/g, '');
  const hasPhoneNumber = whatsappNumber.length > 0;

  const handleFormSubmit = async (data: CustomerFormData) => {
    if (onSubmit) {
      await onSubmit(data);
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('forms.customer.code3')}</label>
            <input 
              data-test-id="customer-form-code" 
              {...register('code')} 
              maxLength={3}
              placeholder={t('forms.common.autoGenerated')}
              disabled={true}
              className="mt-1 block w-full rounded-md border border-gray-300 p-2 bg-gray-100 text-gray-500 cursor-not-allowed uppercase" 
            />
            <p className="text-xs text-gray-500 mt-1">{t('forms.common.generatedAutomatically')}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('forms.customer.name')}</label>
            <input 
              data-test-id="customer-form-name" 
              {...register('name')} 
              disabled={readOnly}
              className="mt-1 block w-full rounded-md border border-gray-300 p-2 disabled:bg-gray-50 disabled:text-gray-500" 
            />
            {errors.name && <p className="text-red-600 text-sm">{errors.name.message}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('forms.common.email')}</label>
            <div className="flex gap-2 mt-1">
              <input 
                data-test-id="customer-form-email" 
                {...register('email')} 
                disabled={readOnly}
                maxLength={EMAIL_MAX_LENGTH}
                onPaste={(e) => {
                  const pasted = e.clipboardData.getData('text');
                  const sanitized = enforceEmailMaxLength(pasted);
                  if (sanitized !== pasted) {
                    e.preventDefault();
                    setValue('email', sanitized, { shouldValidate: true, shouldDirty: true });
                  }
                }}
                onInput={(e) => {
                  const el = e.currentTarget;
                  const raw = el.value;
                  const sanitized = enforceEmailMaxLength(raw);
                  if (sanitized !== raw) {
                    el.value = sanitized;
                    setValue('email', sanitized, { shouldValidate: true, shouldDirty: true });
                  }
                }}
                className="block w-full rounded-md border border-gray-300 p-2 disabled:bg-gray-50 disabled:text-gray-500" 
              />
              <a 
                href={email ? `mailto:${email}` : '#'}
                onClick={(e) => {
                  if (!email) {
                    e.preventDefault();
                  }
                }}
                aria-disabled={!email}
                tabIndex={email ? 0 : -1}
                className={[
                  'flex items-center justify-center p-2 rounded-md transition-colors',
                  email
                    ? 'text-primary-600 bg-primary-50 hover:bg-primary-100'
                    : 'text-gray-300 bg-gray-50 cursor-not-allowed pointer-events-none',
                ].join(' ')}
                title={email ? t('forms.actions.sendEmail') : t('forms.customer.hints.enterEmailToSend')}
              >
                <Mail className="h-5 w-5" />
              </a>
            </div>
            <div className="mt-1 flex items-start justify-between gap-2">
              {errors.email ? (
                <p className="text-red-600 text-sm">{errors.email.message}</p>
              ) : (
                <span />
              )}
              <p
                data-test-id="customer-form-email-char-counter"
                className={`text-xs text-right shrink-0 ${emailLength === EMAIL_MAX_LENGTH ? 'text-amber-600' : 'text-gray-500'}`}
              >
                {t('forms.customer.emailCounter', { count: emailLength, max: EMAIL_MAX_LENGTH })}
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">{t('forms.common.phone')}</label>
            <div className="flex gap-2 mt-1">
              <input 
                data-test-id="customer-form-phone" 
                {...register('phone')}
                inputMode="tel"
                disabled={readOnly}
                onKeyDown={(e) => {
                  if (/^[A-Za-z]$/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                onPaste={(e) => {
                  const pasted = e.clipboardData.getData('text');
                  if (/[A-Za-z]/.test(pasted)) {
                    e.preventDefault();
                    const sanitized = pasted.replace(/[A-Za-z]/g, '');
                    setValue('phone', sanitized, { shouldValidate: true, shouldDirty: true });
                  }
                }}
                onInput={(e) => {
                  const el = e.currentTarget;
                  const raw = el.value;
                  const sanitized = raw.replace(/[A-Za-z]/g, '');
                  if (sanitized !== raw) {
                    el.value = sanitized;
                    setValue('phone', sanitized, { shouldValidate: true, shouldDirty: true });
                  }
                }}
                className="block w-full rounded-md border border-gray-300 p-2 disabled:bg-gray-50 disabled:text-gray-500" 
              />
              <>
                <a
                  href={hasPhoneNumber ? `https://wa.me/${whatsappNumber}` : '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    if (!hasPhoneNumber) {
                      e.preventDefault();
                    }
                  }}
                  aria-disabled={!hasPhoneNumber}
                  tabIndex={hasPhoneNumber ? 0 : -1}
                  className={[
                    'flex items-center justify-center p-2 rounded-md',
                    hasPhoneNumber
                      ? 'text-green-600 bg-green-50 hover:bg-green-100'
                      : 'text-gray-300 bg-gray-50 cursor-not-allowed pointer-events-none',
                  ].join(' ')}
                  title={hasPhoneNumber ? t('forms.actions.openWhatsapp') : t('forms.customer.hints.enterPhoneForWhatsapp')}
                >
                  <MessageCircle className="h-5 w-5" />
                </a>
                <a
                  href={hasPhoneNumber ? `tel:${phone}` : '#'}
                  onClick={(e) => {
                    if (!hasPhoneNumber) {
                      e.preventDefault();
                    }
                  }}
                  aria-disabled={!hasPhoneNumber}
                  tabIndex={hasPhoneNumber ? 0 : -1}
                  className={[
                    'flex items-center justify-center p-2 rounded-md',
                    hasPhoneNumber
                      ? 'text-blue-600 bg-blue-50 hover:bg-blue-100'
                      : 'text-gray-300 bg-gray-50 cursor-not-allowed pointer-events-none',
                  ].join(' ')}
                  title={hasPhoneNumber ? t('forms.actions.call') : t('forms.customer.hints.enterPhoneForCall')}
                >
                  <Phone className="h-5 w-5" />
                </a>
              </>
            </div>
            {errors.phone && <p className="text-red-600 text-sm">{errors.phone.message}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <div className="flex h-7 items-center">
              <label className="block text-sm font-medium text-gray-700">{t('forms.customer.billingAddress')}</label>
            </div>
            <textarea
              data-test-id="customer-form-billing-address"
              {...register('billingAddress')} 
              disabled={readOnly}
              rows={4}
              className="mt-1 block w-full rounded-md border border-gray-300 p-2 disabled:bg-gray-50 disabled:text-gray-500" 
            />
            {errors.billingAddress && <p className="text-red-600 text-sm">{errors.billingAddress.message}</p>}
          </div>

          <div>
            <div className="flex h-7 items-center justify-between gap-2">
              <label className="block text-sm font-medium text-gray-700">{t('forms.customer.shippingAddress')}</label>
              {!readOnly && (
                <label className="flex shrink-0 items-center gap-1.5 text-xs text-gray-600 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={isShippingSameAsBilling}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setIsShippingSameAsBilling(checked);
                      if (checked) {
                        setValue('shippingAddress', billingAddress || '', { shouldValidate: true });
                      } else {
                        setValue('shippingAddress', '', { shouldValidate: true });
                      }
                    }}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  {t('forms.customer.sameAsBilling')}
                </label>
              )}
            </div>
            <textarea
              data-test-id="customer-form-shipping-address"
              {...register('shippingAddress')}
              disabled={readOnly || isShippingSameAsBilling}
              rows={4}
              className="mt-1 block w-full rounded-md border border-gray-300 p-2 disabled:bg-gray-50 disabled:text-gray-500"
            />
            {errors.shippingAddress && <p className="text-red-600 text-sm">{errors.shippingAddress.message}</p>}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">{t('forms.customer.messageOnInvoice')}</label>
          <textarea
            data-test-id="customer-form-message-on-invoice"
            {...register('messageOnInvoice')}
            disabled={readOnly}
            className="mt-1 block w-full rounded-md border border-gray-300 p-2 disabled:bg-gray-50 disabled:text-gray-500"
            rows={3}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">{t('forms.common.internalNotes')}</label>
          <textarea 
            data-test-id="customer-form-internal-notes" 
            {...register('internalNotes')} 
            disabled={readOnly}
            className="mt-1 block w-full rounded-md border border-gray-300 p-2 disabled:bg-gray-50 disabled:text-gray-500" 
            rows={3} 
          />
        </div>
      </div>

      {readOnly && (
        <div className="mt-8 border-t pt-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">{t('forms.customer.salesOrderHistory')}</h3>
          {loadingHistory ? (
            <LoadingSpinner text={t('forms.common.loadingHistory')} />
          ) : salesOrders.length === 0 ? (
            <p className="text-gray-500 text-sm italic">{t('forms.customer.emptySalesOrders')}</p>
          ) : (
            <div className="overflow-x-auto border rounded-md">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('forms.common.orderNumber')}</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('forms.common.date')}</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('forms.common.status')}</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('forms.common.amount')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {salesOrders.map((so) => (
                    <tr key={so.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm font-medium text-gray-900">{so.orderNumber}</td>
                      <td className="px-4 py-2 text-sm text-gray-500">{new Date(so.orderDate).toLocaleDateString()}</td>
                      <td className="px-4 py-2 text-sm">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                          ${so.status === 'Delivered' ? 'bg-green-100 text-green-800' : 
                            so.status === 'Sent' ? 'bg-blue-100 text-blue-800' : 
                            so.status === 'Cancelled' ? 'bg-red-100 text-red-800' : 
                            'bg-gray-100 text-gray-800'}`}>
                          {t(`forms.customer.soStatus.${so.status}`, undefined, so.status)}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-500 text-right">{formatCurrency(so.totalAmount, currencySymbol)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-4">
        <button type="button" data-test-id="customer-form-cancel" onClick={onCancel} className="px-4 py-2 border rounded-md">
          {readOnly ? t('forms.actions.close') : t('auth.common.cancel')}
        </button>
        {!readOnly && (
          <button type="submit" data-test-id="customer-form-submit" disabled={isSubmitting} className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700">
            {isSubmitting ? t('forms.actions.saving') : t('forms.actions.save')}
          </button>
        )}
      </div>
    </form>
  );
};
